import { useState, useEffect } from "react";
import { giftRequestsService } from "@/api/awards";
import { FiClock, FiCheckCircle, FiXCircle, FiAlertCircle, FiGift } from "react-icons/fi";
import TaleButton from "@components/TaleButton";

const MyGiftRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalPages: 0,
    totalItems: 0,
  });

  const loadRequests = async (page = 0) => {
    setLoading(true);
    try {
      const response = await giftRequestsService.getMyGiftRequests(page, 10);
      setRequests(response.result || []);
      setPagination({
        currentPage: response.currentPage,
        totalPages: response.totalPages,
        totalItems: response.totalItems,
      });
    } catch (error) {
      console.error("Failed to load requests:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { icon: <FiClock className="w-4 h-4" />, color: "bg-yellow-100 text-yellow-800", label: "Pending" },
      ACCEPTED: { icon: <FiCheckCircle className="w-4 h-4" />, color: "bg-green-100 text-green-800", label: "Accepted" },
      REJECTED: { icon: <FiXCircle className="w-4 h-4" />, color: "bg-red-100 text-red-800", label: "Rejected" },
      CANCELLED: { icon: <FiAlertCircle className="w-4 h-4" />, color: "bg-gray-100 text-gray-800", label: "Cancelled" }
    };

    const config = statusConfig[status] || statusConfig.PENDING;

    return (
        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        {config.icon}
          {config.label}
      </span>
    );
  };

  const handleCancelRequest = async (id) => {
    if (!window.confirm("Are you sure you want to cancel this request?")) return;

    try {
      await giftRequestsService.cancelGiftRequest(id);
      alert("Request cancelled successfully!");
      loadRequests();
    } catch (error) {
      alert(`Failed to cancel request: ${error.message}`);
    }
  };

  return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">My Gift Requests</h1>
            <p className="text-gray-600">Track your gift requests and their status</p>
          </div>
        </div>

        {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        ) : (
            <>
              <div className="bg-white rounded-2xl overflow-hidden border border-gray-200">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gift</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Points Used</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                    {requests.map((request) => (
                        <tr key={request.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{request.giftName}</div>
                              <div className="text-sm text-gray-500">Notes: {request.notes || "No notes"}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-1">
                              <FiGift className="w-4 h-4 text-amber-500" />
                              <span className="font-semibold">{request.pointsUsed}</span>
                              <span className="text-gray-500">points</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(request.status)}
                            {request.rejectionReason && (
                                <div className="text-xs text-red-600 mt-1">Reason: {request.rejectionReason}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(request.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            {request.status === "PENDING" && (
                                <TaleButton
                                    variant="outlined"
                                    size="sm"
                                    onClick={() => handleCancelRequest(request.id)}
                                    className="!border-red-200 !text-red-600 hover:!bg-red-50"
                                >
                                  Cancel
                                </TaleButton>
                            )}
                          </td>
                        </tr>
                    ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {pagination.totalPages > 1 && (
                  <div className="flex justify-center items-center gap-2 mt-8">
                    <button
                        onClick={() => loadRequests(pagination.currentPage - 1)}
                        disabled={pagination.currentPage === 0}
                        className="px-4 py-2 rounded-lg border border-gray-300 disabled:opacity-50"
                    >
                      Previous
                    </button>

                    <span className="px-4 py-2">
                Page {pagination.currentPage + 1} of {pagination.totalPages}
              </span>

                    <button
                        onClick={() => loadRequests(pagination.currentPage + 1)}
                        disabled={pagination.currentPage >= pagination.totalPages - 1}
                        className="px-4 py-2 rounded-lg border border-gray-300 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
              )}

              {requests.length === 0 && !loading && (
                  <div className="text-center py-12">
                    <FiGift className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-gray-700 mb-2">No gift requests</h3>
                    <p className="text-gray-500">You haven't made any gift requests yet</p>
                  </div>
              )}
            </>
        )}
      </div>
  );
};

export default MyGiftRequests;
